import React, { useState, useMemo } from 'react';
import {
  Trophy, Calendar, DollarSign, Users, MapPin, Clock,
  ChevronDown, ChevronUp, ChevronRight, CheckCircle, XCircle, AlertCircle,
  TrendingUp, PieChart, List, Grid, ArrowLeft, Edit2,
  UserCheck, UserX, HelpCircle, Utensils, Shield, ClipboardList, Upload, Download, Plus, Trash2
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { doc, updateDoc, arrayUnion, addDoc, collection } from 'firebase/firestore';
import { db } from '../../services/firebase';
import TournamentRosterModal from './TournamentRosterModal';
import ImportUmpireScheduleModal from './ImportUmpireScheduleModal';
import ImportScheduleModal from './ImportScheduleModal';
import AddUmpireAssignmentModal from './AddUmpireAssignmentModal';

const TournamentDetailView = ({
  tournament,
  games,
  expenses,
  teams,
  walletTransactions,
  players: allPlayers = [],
  isTreasurer = false,
  isAdmin = false,
  currentUserId = null,
  currentUserEmail = null,
  onBack,
  onEditGame,
  onSelectSquad,
  onImportGames
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedGames, setExpandedGames] = useState({});
  const [expandedPlayerCosts, setExpandedPlayerCosts] = useState({});
  const [currentTournament, setCurrentTournament] = useState(tournament);
  const [rosterModalOpen, setRosterModalOpen] = useState(false);
  const [selectedTeamForRoster, setSelectedTeamForRoster] = useState(null);
  const [showUmpireImportModal, setShowUmpireImportModal] = useState(false);
  const [showUmpireAddModal, setShowUmpireAddModal] = useState(false);
  const [showImportGamesModal, setShowImportGamesModal] = useState(false);
  const [showLateFeesDetail, setShowLateFeesDetail] = useState(false);

  // Credit Offset Modal State
  const [showCreditOffsetModal, setShowCreditOffsetModal] = useState(false);
  const [creditOffsetTarget, setCreditOffsetTarget] = useState(null);
  const [creditOffsetAmount, setCreditOffsetAmount] = useState('');
  const [creditOffsetNote, setCreditOffsetNote] = useState('');
  const [creditOffsetLoading, setCreditOffsetLoading] = useState(false);

  // Identify Unknown Player State
  const [showIdentifyModal, setShowIdentifyModal] = useState(false);
  const [identifyGhostId, setIdentifyGhostId] = useState(null);
  const [identifySearch, setIdentifySearch] = useState('');
  const [identifyLoading, setIdentifyLoading] = useState(false);

  // Mark Tournament Paid State
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState(null); // { player, balance }
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [markPaidNote, setMarkPaidNote] = useState('');
  const [markPaidLoading, setMarkPaidLoading] = useState(false);

  // Sync state if prop changes
  useMemo(() => setCurrentTournament(tournament), [tournament]);

  // Filter data for this tournament
  const tournamentGames = useMemo(() =>
    games.filter(g => g.tournamentId === tournament.id)
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [games, tournament.id]
  );

  const tournamentExpenses = useMemo(() =>
    expenses.filter(e => {
      // Direct tournament expenses
      if (e.tournamentId === tournament.id) return true;
      // Game-based expenses for tournament games
      const gameIds = tournamentGames.map(g => g.id);
      return e.gameId && gameIds.includes(e.gameId);
    }),
    [expenses, tournament.id, tournamentGames]
  );

  // Calculate tournament statistics
  const stats = useMemo(() => {
    const now = new Date();
    const completedGames = tournamentGames.filter(g => new Date(g.date) < now);
    const upcomingGames = tournamentGames.filter(g => new Date(g.date) >= now);

    // Get all unique players who participated
    const allPlayerIds = new Set();
    const playerGameCount = {};
    const playerExpenses = {};
    const playerExpensesBreakdown = {}; // Mapping: playerId -> { [gameId]: { gameId, gameName, gameDate, perGameShare, tourneyShare, lateFee, total } }

    const initBreakdown = (playerId, game) => {
      if (!playerExpensesBreakdown[playerId]) {
        playerExpensesBreakdown[playerId] = {};
      }
      if (!playerExpensesBreakdown[playerId][game.id]) {
        playerExpensesBreakdown[playerId][game.id] = {
          gameId: game.id,
          gameName: game.name || `${game.homeTeam} vs ${game.awayTeam}`,
          gameDate: game.date,
          perGameShare: 0,
          tourneyShare: 0,
          lateFee: 0,
          total: 0
        };
      }
    };

    tournamentGames.forEach(game => {
      (game.playing11 || []).forEach(playerId => {
        allPlayerIds.add(playerId);
        playerGameCount[playerId] = (playerGameCount[playerId] || 0) + 1;
        initBreakdown(playerId, game);
      });
    });

    // Calculate expenses per player
    tournamentExpenses.forEach(expense => {
      const game = expense.gameId ? tournamentGames.find(g => g.id === expense.gameId) : null;
      
      if (game && game.playing11 && game.playing11.length > 0) {
        // PER-GAME EXPENSE
        const squadSize = game.playing11.length;
        const superSubCount = game.superSubs?.length || 0;
        const regularCount = squadSize - superSubCount;
        const totalShares = regularCount + (superSubCount * 0.5);

        game.playing11.forEach(playerId => {
          const isSuperSub = game.superSubs?.includes(playerId);
          const share = isSuperSub
            ? (expense.amount / totalShares) * 0.5
            : expense.amount / totalShares;
          const roundedShare = Math.round(share * 100) / 100;
          playerExpenses[playerId] = (playerExpenses[playerId] || 0) + roundedShare;

          initBreakdown(playerId, game);
          playerExpensesBreakdown[playerId][game.id].perGameShare += roundedShare;
          playerExpensesBreakdown[playerId][game.id].total += roundedShare;
        });
      } else if (!game || expense.level === 'tournament') {
        // TOURNAMENT-WIDE EXPENSE
        // Divide by number of games that have a roster, then divide among players in each game
        const validGames = tournamentGames.filter(g => g.playing11 && g.playing11.length > 0);
        const numGames = validGames.length;
        
        if (numGames > 0) {
          const expensePerGame = expense.amount / numGames;
          
          validGames.forEach(g => {
            const squadSize = g.playing11.length;
            const superSubCount = g.superSubs?.length || 0;
            const regularCount = squadSize - superSubCount;
            const totalShares = regularCount + (superSubCount * 0.5);

            g.playing11.forEach(playerId => {
              const isSuperSub = g.superSubs?.includes(playerId);
              const share = isSuperSub
                ? (expensePerGame / totalShares) * 0.5
                : expensePerGame / totalShares;
              const roundedShare = Math.round(share * 100) / 100;
              playerExpenses[playerId] = (playerExpenses[playerId] || 0) + roundedShare;

              initBreakdown(playerId, g);
              playerExpensesBreakdown[playerId][g.id].tourneyShare += roundedShare;
              playerExpensesBreakdown[playerId][g.id].total += roundedShare;
            });
          });
        }
      }
    });

    // Calculate late fees per player
    let totalLateFees = 0;
    const lateFeesList = [];

    tournamentGames.forEach(game => {
      if (game.lateArrivals) {
        Object.keys(game.lateArrivals).forEach(playerId => {
          if (game.lateArrivals[playerId]) {
            const lateFee = game.lateFeeAmount ?? 5;
            playerExpenses[playerId] = (playerExpenses[playerId] || 0) + lateFee;
            totalLateFees += lateFee;
            lateFeesList.push({
              gameId: game.id,
              gameName: game.name || `${game.homeTeam} vs ${game.awayTeam}`,
              playerId,
              amount: lateFee
            });

            initBreakdown(playerId, game);
            playerExpensesBreakdown[playerId][game.id].lateFee += lateFee;
            playerExpensesBreakdown[playerId][game.id].total += lateFee;
          }
        });
      }
    });

    let totalExpenses = tournamentExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    // Add late fees to total tournament expenses
    totalExpenses += totalLateFees;

    // Get total collected for this tournament specifically.
    // Only count payment/waiver transactions tagged to this tournament or its games.
    const totalCollected = walletTransactions
      .filter(t =>
        (t.type === 'payment' || t.type === 'waiver') &&
        t.status === 'approved' &&
        t.amount < 0 &&
        !t.isDonation &&
        (t.tournamentId === tournament.id || tournamentGames.some(g => g.id === t.gameId))
      )
      .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

    return {
      totalGames: tournamentGames.length,
      completedGames: completedGames.length,
      upcomingGames: upcomingGames.length,
      totalPlayers: allPlayerIds.size,
      totalExpenses,
      totalCollected,
      receivables: totalExpenses - totalCollected,
      playerGameCount,
      playerExpenses,
      allPlayerIds: Array.from(allPlayerIds),
      totalLateFees,
      lateFeesList,
      playerExpensesBreakdown
    };
  }, [tournamentGames, tournamentExpenses, walletTransactions, tournament.id]);

  // Get player info from teams
  const getPlayerInfo = (playerId) => {
    for (const team of teams) {
      const player = team.players?.find(p => p.id === playerId || p.userId === playerId);
      if (player) {
        return { ...player, teamName: team.name };
      }
    }
    return { name: 'Unknown Player', email: '', teamName: 'Unknown' };
  };

  // --- CREDIT OFFSET HELPERS ---

  const getClubNetCredit = (playerId, playerEmail) => {
    const info = getPlayerInfo(playerId);
    const pName = info?.name;
    // expense_reimbursement = positive (club owes player), reimbursement_paid = negative (club already paid)
    const net = walletTransactions
      .filter(t =>
        t.status === 'approved' &&
        (t.type === 'expense_reimbursement' || t.type === 'reimbursement_paid') &&
        (
          t.userId === playerId || 
          (playerEmail && t.playerEmail?.toLowerCase() === playerEmail.toLowerCase()) ||
          (pName && t.playerName?.toLowerCase().trim() === pName.toLowerCase().trim())
        )
      )
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    return Math.max(0, net); // positive net = club still owes the player
  };

  const openCreditOffsetModal = (player) => {
    const tournamentBalance = player.totalOwed - player.paid;
    const clubCredit = getClubNetCredit(player.id, player.email);
    const defaultOffset = Math.min(tournamentBalance, clubCredit);
    setCreditOffsetTarget({ player, tournamentBalance, clubCredit });
    setCreditOffsetAmount(defaultOffset.toFixed(2));
    setCreditOffsetNote('');
    setShowCreditOffsetModal(true);
  };

  const handleCreditOffset = async () => {
    if (!creditOffsetTarget || creditOffsetLoading) return;
    const { player, tournamentBalance, clubCredit } = creditOffsetTarget;
    const offsetAmount = parseFloat(creditOffsetAmount);

    if (isNaN(offsetAmount) || offsetAmount <= 0) {
      alert('Please enter a valid offset amount.');
      return;
    }
    if (offsetAmount > tournamentBalance + 0.01) {
      alert(`Offset cannot exceed the tournament balance of ${formatCurrency(tournamentBalance)}.`);
      return;
    }
    if (offsetAmount > clubCredit + 0.01) {
      alert(`Offset cannot exceed the available club credit of ${formatCurrency(clubCredit)}.`);
      return;
    }

    const noteText = creditOffsetNote.trim();
    setCreditOffsetLoading(true);
    try {
      // 1. Tournament waiver — reduces what player owes
      await addDoc(collection(db, 'walletTransactions'), {
        userId: player.id,
        clubId: tournament.clubId,
        type: 'waiver',
        amount: -offsetAmount,
        description: `Credit offset applied toward ${tournament.name} fees${noteText ? ` — ${noteText}` : ''}`,
        status: 'approved',
        tournamentId: tournament.id,
        isCreditOffset: true,
        note: noteText || null,
        createdAt: new Date().toISOString(),
        recordedBy: currentUserId || 'admin'
      });

      // 2. Reimbursement settlement — reduces what club owes the player
      await addDoc(collection(db, 'walletTransactions'), {
        userId: player.id,
        clubId: tournament.clubId,
        type: 'reimbursement_paid',
        amount: -offsetAmount,
        description: `Credit offset: ${formatCurrency(offsetAmount)} applied to ${tournament.name} fees instead of cash payout${noteText ? ` — ${noteText}` : ''}`,
        status: 'approved',
        isCreditOffset: true,
        note: noteText || null,
        createdAt: new Date().toISOString(),
        recordedBy: currentUserId || 'admin'
      });

      alert(`✅ Credit offset of ${formatCurrency(offsetAmount)} applied for ${player.name}.`);
      setShowCreditOffsetModal(false);
      setCreditOffsetTarget(null);
      setCreditOffsetAmount('');
      setCreditOffsetNote('');
    } catch (err) {
      console.error('Credit offset failed:', err);
      alert('Failed to apply credit offset. Please try again.');
    } finally {
      setCreditOffsetLoading(false);
    }
  };

  // --- IDENTIFY UNKNOWN PLAYER ---
  const openIdentifyModal = (ghostId) => {
    setIdentifyGhostId(ghostId);
    setIdentifySearch('');
    setShowIdentifyModal(true);
  };

  const handleIdentifyPlayer = async (realPlayer) => {
    if (!identifyGhostId || identifyLoading) return;
    setIdentifyLoading(true);
    try {
      const affectedGames = games.filter(g =>
        g.tournamentId === tournament.id &&
        (
          g.playing11?.includes(identifyGhostId) ||
          g.superSubs?.includes(identifyGhostId) ||
          (g.lateArrivals && identifyGhostId in g.lateArrivals)
        )
      );

      const replaceId = (arr) =>
        arr ? arr.map(id => id === identifyGhostId ? realPlayer.id : id) : arr;

      const updates = affectedGames.map(g => {
        const patch = {};
        if (g.playing11?.includes(identifyGhostId)) patch.playing11 = replaceId(g.playing11);
        if (g.superSubs?.includes(identifyGhostId)) patch.superSubs = replaceId(g.superSubs);
        if (g.lateArrivals && identifyGhostId in g.lateArrivals) {
          const newLate = { ...g.lateArrivals };
          newLate[realPlayer.id] = newLate[identifyGhostId];
          delete newLate[identifyGhostId];
          patch.lateArrivals = newLate;
        }
        return updateDoc(doc(db, 'games', g.id), patch);
      });

      await Promise.all(updates);
      alert(`✅ ${affectedGames.length} game(s) updated — ${realPlayer.name} now assigned.`);
      setShowIdentifyModal(false);
      setIdentifyGhostId(null);
    } catch (err) {
      console.error('Identify player failed:', err);
      alert('Failed to assign player. Please try again.');
    } finally {
      setIdentifyLoading(false);
    }
  };

  // --- MARK TOURNAMENT PAID ---
  const openMarkPaidModal = (player, balance) => {
    setMarkPaidTarget({ player, balance });
    setMarkPaidAmount(balance.toFixed(2));
    setMarkPaidNote('');
    setShowMarkPaidModal(true);
  };

  const handleMarkTournamentPaid = async () => {
    if (!markPaidTarget || markPaidLoading) return;
    const { player } = markPaidTarget;
    const amount = parseFloat(markPaidAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount.');
      return;
    }
    const noteText = markPaidNote.trim();
    setMarkPaidLoading(true);
    try {
      await addDoc(collection(db, 'walletTransactions'), {
        userId: player.id,
        playerEmail: player.email || null,
        clubId: tournament.clubId,
        type: 'payment',
        amount: -amount,
        description: `Tournament payment recorded${noteText ? ` — ${noteText}` : ''}`,
        status: 'approved',
        tournamentId: tournament.id,
        isManualAttribution: true,
        note: noteText || null,
        createdAt: new Date().toISOString(),
        recordedBy: currentUserId || 'admin'
      });
      alert(`✅ ${formatCurrency(amount)} recorded as paid for ${player.name}.`);
      setShowMarkPaidModal(false);
      setMarkPaidTarget(null);
      setMarkPaidAmount('');
      setMarkPaidNote('');
    } catch (err) {
      console.error('Mark paid failed:', err);
      alert('Failed to record payment. Please try again.');
    } finally {
      setMarkPaidLoading(false);
    }
  };

  const playerStats = useMemo(() => {
    return stats.allPlayerIds.map(playerId => {
      const info = getPlayerInfo(playerId);
      return {
        id: playerId,
        name: info.name,
        email: info.email,
        teamName: info.teamName,
        gamesPlayed: stats.playerGameCount[playerId] || 0,
        totalOwed: stats.playerExpenses[playerId] || 0,
        breakdown: stats.playerExpensesBreakdown[playerId] || {},
        // Calculate paid amount from wallet transactions.
        // Only count transactions explicitly tagged to this tournament or one of its games.
        // Payment submissions must include a tournamentId (set at submission time) so that
        // approved wallet transactions can be correctly matched here.
        paid: walletTransactions
          .filter(t =>
            (
              t.userId === playerId || 
              (info.email && t.playerEmail?.toLowerCase() === info.email.toLowerCase()) ||
              (info.name && t.playerName?.toLowerCase().trim() === info.name.toLowerCase().trim())
            ) &&
            (t.type === 'payment' || t.type === 'waiver') &&
            t.status === 'approved' &&
            t.amount < 0 &&
            !t.isDonation &&
            (t.tournamentId === tournament.id || tournamentGames.some(g => g.id === t.gameId))
          )
          .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0)
      };
    }).sort((a, b) => b.gamesPlayed - a.gamesPlayed);
  }, [stats, teams, walletTransactions, tournament.id, tournamentGames]);

  const toggleGameExpand = (gameId) => {
    setExpandedGames(prev => ({ ...prev, [gameId]: !prev[gameId] }));
  };

  const togglePlayerCostExpand = (playerId) => {
    setExpandedPlayerCosts(prev => ({ ...prev, [playerId]: !prev[playerId] }));
  };

  const getGameStatus = (game) => {
    const now = new Date();
    const gameDate = new Date(game.date);
    if (gameDate < now) {
      return { label: 'Completed', color: 'green', icon: CheckCircle };
    } else if (gameDate.toDateString() === now.toDateString()) {
      return { label: 'Today', color: 'yellow', icon: Clock };
    } else {
      return { label: 'Upcoming', color: 'blue', icon: Calendar };
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'games', label: 'Games', icon: Calendar },
    { id: 'teams', label: 'Teams', icon: Shield },
    { id: 'finances', label: 'Finances', icon: DollarSign },
    { id: 'players', label: 'Players', icon: Users },
    { id: 'umpiring', label: 'Umpiring', icon: ClipboardList },
  ];

  const handleOpenRosterModal = (team) => {
    setSelectedTeamForRoster(team);
    setRosterModalOpen(true);
  };

  const handleSaveRoster = async (playerIds) => {
    if (!selectedTeamForRoster || !currentTournament) return;

    try {
      const teamId = selectedTeamForRoster.id;
      const tournamentRef = doc(db, 'tournaments', currentTournament.id);

      let updateData = {
        [`rosters.${teamId}`]: playerIds
      };

      // If team is not in tournament, add it to the 'teams' array
      if (!currentTournament.teams?.includes(teamId)) {
        updateData.teams = arrayUnion(teamId);

        // Optimistic update for teams array and rosters
        setCurrentTournament(prev => ({
          ...prev,
          teams: [...(prev.teams || []), teamId],
          rosters: {
            ...(prev.rosters || {}),
            [teamId]: playerIds
          }
        }));
      } else {
        // Standard optimistic update for rosters only
        setCurrentTournament(prev => ({
          ...prev,
          rosters: {
            ...(prev.rosters || {}),
            [teamId]: playerIds
          }
        }));
      }

      await updateDoc(tournamentRef, updateData);

    } catch (error) {
      console.error("Error saving roster:", error);
      alert("Failed to save roster.");
    }
  };

  const handleImportUmpiringAssignments = async (assignments) => {
    try {
      if (!currentTournament) return;
      const tournamentRef = doc(db, 'tournaments', currentTournament.id);

      const newAssignments = assignments.map(a => ({
        ...a,
        importedAt: new Date().toISOString()
      }));

      await updateDoc(tournamentRef, {
        umpiringAssignments: arrayUnion(...newAssignments)
      });

      // Optimistic update
      setCurrentTournament(prev => ({
        ...prev,
        umpiringAssignments: [...(prev.umpiringAssignments || []), ...newAssignments]
      }));

    } catch (error) {
      console.error("Error importing umpiring assignments:", error);
      alert("Failed to import umpiring assignments.");
    }
  };

  const handleAddUmpiringAssignment = async (newAssignment) => {
    try {
      if (!currentTournament) return;
      const tournamentRef = doc(db, 'tournaments', currentTournament.id);

      const assignmentWithId = {
        ...newAssignment,
        id: Math.random().toString(36).substr(2, 9),
        importedAt: new Date().toISOString()
      };

      await updateDoc(tournamentRef, {
        umpiringAssignments: arrayUnion(assignmentWithId)
      });

      // Optimistic update
      setCurrentTournament(prev => ({
        ...prev,
        umpiringAssignments: [...(prev.umpiringAssignments || []), assignmentWithId]
      }));

    } catch (error) {
      console.error("Error adding umpiring assignment:", error);
      alert("Failed to add umpiring assignment.");
    }
  };

  const handleDeleteUmpiringAssignment = async (assignmentId, idx) => {
    if (!window.confirm("Are you sure you want to delete this umpiring assignment?")) return;
    try {
      if (!currentTournament) return;
      const tournamentRef = doc(db, 'tournaments', currentTournament.id);
      
      const updatedAssignments = (currentTournament.umpiringAssignments || []).filter((a, i) => {
        if (assignmentId && a.id) {
          return a.id !== assignmentId;
        }
        return i !== idx;
      });

      await updateDoc(tournamentRef, {
        umpiringAssignments: updatedAssignments
      });

      // Optimistic update
      setCurrentTournament(prev => ({
        ...prev,
        umpiringAssignments: updatedAssignments
      }));

    } catch (error) {
      console.error("Error deleting umpiring assignment:", error);
      alert("Failed to delete umpiring assignment.");
    }
  };

  const handleExportExcel = () => {
    // 1. Player Summary Sheet
    const playerSummaryData = [
      ['Player Name', 'Team Name', 'Games Played', 'Total Amount Owed', 'Amount Paid', 'Outstanding Balance']
    ];
    
    playerStats.forEach(player => {
      const outstanding = player.totalOwed - player.paid;
      playerSummaryData.push([
        player.name,
        player.teamName,
        player.gamesPlayed,
        player.totalOwed,
        player.paid,
        outstanding
      ]);
    });

    // 2. Game Squads Sheet
    const gameSquadsData = [
      ['Game Name', 'Date', 'Venue', 'Player Name', 'Role']
    ];

    tournamentGames.forEach(game => {
      if (game.playing11) {
        game.playing11.forEach(playerId => {
          const player = getPlayerInfo(playerId);
          const isSuperSub = game.superSubs?.includes(playerId);
          gameSquadsData.push([
            game.name || `${game.homeTeam} vs ${game.awayTeam}`,
            new Date(game.date).toLocaleDateString(),
            game.venue || '',
            player.name,
            isSuperSub ? 'Super Sub' : 'Regular'
          ]);
        });
      }
    });

    // 3. Expenses Sheet
    const expensesData = [
      ['Game / Scope', 'Date', 'Description', 'Amount', 'Added By']
    ];

    tournamentExpenses.forEach(expense => {
      let scope = 'Tournament General';
      let date = expense.date ? new Date(expense.date).toLocaleDateString() : '';
      if (expense.gameId) {
        const game = tournamentGames.find(g => g.id === expense.gameId);
        if (game) {
          scope = game.name || `${game.homeTeam} vs ${game.awayTeam}`;
          if (!date && game.date) {
            date = new Date(game.date).toLocaleDateString();
          }
        }
      }
      expensesData.push([
        scope,
        date,
        expense.description || '',
        expense.amount || 0,
        expense.createdByName || expense.createdBy || 'Admin'
      ]);
    });

    // Create workbook and add sheets
    const wb = XLSX.utils.book_new();
    
    const wsPlayerSummary = XLSX.utils.aoa_to_sheet(playerSummaryData);
    XLSX.utils.book_append_sheet(wb, wsPlayerSummary, 'Player Summary');

    const wsGameSquads = XLSX.utils.aoa_to_sheet(gameSquadsData);
    XLSX.utils.book_append_sheet(wb, wsGameSquads, 'Game Squads');

    const wsExpenses = XLSX.utils.aoa_to_sheet(expensesData);
    XLSX.utils.book_append_sheet(wb, wsExpenses, 'Expenses');

    // Generate and download
    const fileName = `${tournament.name.replace(/[^a-zA-Z0-9]/g, '_')}_Report.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleSettleTournament = async () => {
    if (window.confirm('Are you sure you want to settle this tournament? This will prevent new expenses from being added and will finalize player balances for all games in this tournament.')) {
      try {
        const tournamentRef = doc(db, 'tournaments', currentTournament.id);
        const updates = {
          isSettled: true,
          settledAt: new Date().toISOString(),
          settledBy: currentUserEmail || 'Treasurer'
        };
        await updateDoc(tournamentRef, updates);
        setCurrentTournament(prev => ({ ...prev, ...updates }));
        alert('Tournament settled successfully! Player balances are now updated.');
      } catch (error) {
        console.error('Error settling tournament:', error);
        alert('Failed to settle tournament. Please try again.');
      }
    }
  };

  const handleUnsettleTournament = async () => {
    if (window.confirm('Are you sure you want to unsettle this tournament? This will allow new expenses to be added/edited and will suspend final player balances until settled again.')) {
      try {
        const tournamentRef = doc(db, 'tournaments', currentTournament.id);
        const updates = {
          isSettled: false,
          unsettledAt: new Date().toISOString(),
          unsettledBy: currentUserEmail || 'Treasurer'
        };
        await updateDoc(tournamentRef, updates);
        setCurrentTournament(prev => ({ ...prev, ...updates }));
        alert('Tournament unsettled successfully! You can now make adjustments.');
      } catch (error) {
        console.error('Error unsettling tournament:', error);
        alert('Failed to unsettle tournament. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="flex justify-between items-start mb-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          
          <div className="flex gap-2">
            {!currentTournament.isSettled && isTreasurer && (
              <button
                onClick={handleSettleTournament}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-colors"
              >
                <CheckCircle size={16} />
                Settle Up
              </button>
            )}
            {currentTournament.isSettled && isTreasurer && (
              <button
                onClick={handleUnsettleTournament}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-colors"
              >
                <XCircle size={16} />
                Revoke Settle Up
              </button>
            )}
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-colors backdrop-blur-sm"
            >
              <Download size={16} />
              Export to Excel
            </button>
          </div>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-white/20 rounded-xl">
                <Trophy size={32} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold">{tournament.name}</h1>
                  {currentTournament.isSettled && (
                    <span className="bg-green-500/20 text-green-100 border border-green-500/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                      <CheckCircle size={14} /> Settled
                    </span>
                  )}
                </div>
                <p className="text-white/80">
                  {tournament.startDate && (
                    <>
                      {formatDate(tournament.startDate, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                      {tournament.endDate && tournament.endDate !== tournament.startDate && (
                        <> - {formatDate(tournament.endDate, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}</>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="flex gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{stats.totalGames}</div>
              <div className="text-sm text-white/70">Games</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{stats.totalPlayers}</div>
              <div className="text-sm text-white/70">Players</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{formatCurrency(stats.totalExpenses)}</div>
              <div className="text-sm text-white/70">Total Cost</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm p-2 flex gap-2 overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-none md:flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${activeTab === tab.id
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="text-green-600" size={20} />
                </div>
                <span className="text-sm text-gray-600">Completed</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.completedGames}</div>
              <div className="text-xs text-gray-500">games finished</div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="text-blue-600" size={20} />
                </div>
                <span className="text-sm text-gray-600">Upcoming</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stats.upcomingGames}</div>
              <div className="text-xs text-gray-500">games remaining</div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="text-green-600" size={20} />
                </div>
                <span className="text-sm text-gray-600">Collected</span>
              </div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalCollected)}</div>
              <div className="text-xs text-gray-500">payments received</div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="text-red-600" size={20} />
                </div>
                <span className="text-sm text-gray-600">Receivables</span>
              </div>
              <div className="text-2xl font-bold text-red-600">{formatCurrency(stats.receivables)}</div>
              <div className="text-xs text-gray-500">pending collection</div>
            </div>
          </div>

          {/* Recent/Upcoming Games Preview */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold mb-4">Games Schedule</h3>
            {tournamentGames.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar size={48} className="mx-auto mb-3 text-gray-300" />
                <p>No games scheduled yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tournamentGames.slice(0, 5).map(game => {
                  const status = getGameStatus(game);
                  const StatusIcon = status.icon;
                  return (
                    <div key={game.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg bg-${status.color}-100`}>
                          <StatusIcon className={`text-${status.color}-600`} size={20} />
                        </div>
                        <div>
                          <div className="font-semibold">{game.name || `${game.homeTeam} vs ${game.awayTeam} `}</div>
                          <div className="text-sm text-gray-500">
                            {formatDate(game.date)} at {game.time}
                            {game.venue && ` • ${game.venue} `}
                          </div>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-${status.color}-100 text-${status.color}-700`}>
                        {status.label}
                      </span>
                    </div>
                  );
                })}
                {tournamentGames.length > 5 && (
                  <button
                    onClick={() => setActiveTab('games')}
                    className="w-full py-2 text-purple-600 hover:text-purple-700 font-semibold text-sm"
                  >
                    View all {tournamentGames.length} games →
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Top Players */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold mb-4">Top Participants</h3>
            {playerStats.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users size={48} className="mx-auto mb-3 text-gray-300" />
                <p>No player data yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {playerStats.slice(0, 5).map((player, idx) => (
                  <div key={player.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
                        } `}>
                        {idx + 1}
                      </div>
                      <div>
                        <div className="font-semibold">{player.name}</div>
                        <div className="text-xs text-gray-500">{player.teamName}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{player.gamesPlayed} games</div>
                      <div className="text-xs text-gray-500">Owes: {formatCurrency(player.totalOwed)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'games' && (
        <div className="space-y-4">
          {/* Header Actions */}
          <div className="flex justify-end mb-4">
            {onImportGames && (
              <button
                onClick={() => setShowImportGamesModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors inline-flex items-center gap-2 text-sm shadow-sm"
              >
                <Upload size={16} />
                Import Schedule
              </button>
            )}
          </div>

          {tournamentGames.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <Calendar size={64} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Games Yet</h3>
              <p className="text-gray-500">Add games to this tournament to see them here.</p>
            </div>
          ) : (
            tournamentGames.map(game => {
              const status = getGameStatus(game);
              const StatusIcon = status.icon;
              const isExpanded = expandedGames[game.id];
              const gameExpenses = tournamentExpenses.filter(e => e.gameId === game.id);
              const totalGameExpense = gameExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

              // Availability stats
              const availabilityStats = {
                available: Object.values(game.availability || {}).filter(a => a.status === 'available').length,
                unavailable: Object.values(game.availability || {}).filter(a => a.status === 'unavailable').length,
                maybe: Object.values(game.availability || {}).filter(a => a.status === 'maybe').length,
              };

              // Meal preference stats
              const mealStats = {
                veg: Object.values(game.mealPreferences || {}).filter(m => m.preference === 'veg').length,
                nonVeg: Object.values(game.mealPreferences || {}).filter(m => m.preference === 'non-veg').length,
                optOut: Object.values(game.mealPreferences || {}).filter(m => m.preference === 'opt-out').length,
              };

              return (
                <div key={game.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  {/* Game Header */}
                  <div
                    className="p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleGameExpand(game.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${status.color === 'green' ? 'bg-green-100' :
                          status.color === 'yellow' ? 'bg-yellow-100' : 'bg-blue-100'
                          } `}>
                          <StatusIcon className={`${status.color === 'green' ? 'text-green-600' :
                            status.color === 'yellow' ? 'text-yellow-600' : 'text-blue-600'
                            } `} size={24} />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg">{game.name || `${game.homeTeam} vs ${game.awayTeam} `}</h4>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(game.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {game.time}
                            </span>
                            {game.venue && (
                              <span className="flex items-center gap-1">
                                <MapPin size={14} />
                                {game.venue}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Squad</div>
                          <div className="font-semibold">{game.playing11?.length || 0} players</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Expenses</div>
                          <div className="font-semibold">{formatCurrency(totalGameExpense)}</div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.color === 'green' ? 'bg-green-100 text-green-700' :
                          status.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                          } `}>
                          {status.label}
                        </span>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t bg-gray-50 p-5">
                      <div className="grid md:grid-cols-3 gap-6">
                        {/* Availability */}
                        <div>
                          <h5 className="font-semibold mb-3 flex items-center gap-2">
                            <UserCheck size={16} className="text-green-600" />
                            Availability Responses
                          </h5>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                              <span className="text-sm">✓ Available</span>
                              <span className="font-semibold text-green-700">{availabilityStats.available}</span>
                            </div>
                            <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                              <span className="text-sm">✗ Unavailable</span>
                              <span className="font-semibold text-red-700">{availabilityStats.unavailable}</span>
                            </div>
                            <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                              <span className="text-sm">? Maybe</span>
                              <span className="font-semibold text-yellow-700">{availabilityStats.maybe}</span>
                            </div>
                          </div>
                        </div>

                        {/* Meal Preferences */}
                        {game.askMealPreference && (
                          <div>
                            <h5 className="font-semibold mb-3 flex items-center gap-2">
                              <Utensils size={16} className="text-orange-600" />
                              Meal Preferences
                            </h5>
                            <div className="space-y-2">
                              <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                                <span className="text-sm">🥗 Vegetarian</span>
                                <span className="font-semibold text-green-700">{mealStats.veg}</span>
                              </div>
                              <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                                <span className="text-sm">🍗 Non-Veg</span>
                                <span className="font-semibold text-orange-700">{mealStats.nonVeg}</span>
                              </div>
                              <div className="flex justify-between items-center p-2 bg-gray-100 rounded">
                                <span className="text-sm">❌ Opt Out</span>
                                <span className="font-semibold text-gray-700">{mealStats.optOut}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Playing 11 */}
                        <div>
                          <h5 className="font-semibold mb-3 flex items-center gap-2">
                            <Users size={16} className="text-blue-600" />
                            Playing Squad ({game.playing11?.length || 0})
                          </h5>
                          {game.playing11 && game.playing11.length > 0 ? (
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {game.playing11.map(playerId => {
                                const player = getPlayerInfo(playerId);
                                const isSuperSub = game.superSubs?.includes(playerId);
                                return (
                                  <div key={playerId} className="flex items-center justify-between p-2 bg-white rounded text-sm">
                                    <span>{player.name}</span>
                                    {isSuperSub && (
                                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                        Super Sub
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">Squad not selected</p>
                          )}
                        </div>
                      </div>

                      {/* Expenses for this game */}
                      {gameExpenses.length > 0 && (
                        <div className="mt-6 pt-4 border-t">
                          <h5 className="font-semibold mb-3 flex items-center gap-2">
                            <DollarSign size={16} className="text-green-600" />
                            Game Expenses
                          </h5>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {gameExpenses.map(expense => (
                              <div key={expense.id} className="p-3 bg-white rounded-lg border">
                                <div className="flex justify-between items-start">
                                  <span className="font-medium text-sm">{expense.description}</span>
                                  <span className="font-semibold text-green-600">{formatCurrency(expense.amount)}</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Added by {expense.createdByName || expense.createdBy}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="mt-4 pt-4 border-t flex gap-3">
                        {onEditGame && (
                          <button
                            onClick={() => onEditGame(game)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center gap-2"
                          >
                            <Edit2 size={14} />
                            Edit Game
                          </button>
                        )}
                        {onSelectSquad && (
                          <button
                            onClick={() => onSelectSquad(game)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 flex items-center gap-2"
                          >
                            <Users size={14} />
                            Select Squad
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'finances' && (
        <div className="space-y-6">
          {/* Financial Summary Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="text-blue-600" size={20} />
                </div>
                <span className="text-gray-600">Total Expenses</span>
              </div>
              <div className="text-3xl font-bold">{formatCurrency(stats.totalExpenses)}</div>
              <div className="text-sm text-gray-500 mt-1">{tournamentExpenses.length} expense items</div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="text-green-600" size={20} />
                </div>
                <span className="text-gray-600">Collected</span>
              </div>
              <div className="text-3xl font-bold text-green-600">{formatCurrency(stats.totalCollected)}</div>
              <div className="text-sm text-gray-500 mt-1">
                {stats.totalExpenses > 0
                  ? `${Math.round((stats.totalCollected / stats.totalExpenses) * 100)}% collection rate`
                  : 'No expenses yet'
                }
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="text-red-600" size={20} />
                </div>
                <span className="text-gray-600">Receivables</span>
              </div>
              <div className="text-3xl font-bold text-red-600">{formatCurrency(stats.receivables)}</div>
              <div className="text-sm text-gray-500 mt-1">pending collection</div>
            </div>
          </div>

          {/* Expense Breakdown by Game */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold mb-4">Expenses by Game</h3>
            {tournamentGames.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No games in this tournament yet</p>
            ) : (
              <div className="space-y-3">
                {tournamentGames.map(game => {
                  const gameExpenses = tournamentExpenses.filter(e => e.gameId === game.id);
                  const sharedTotal = gameExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

                  let gameLateFees = 0;
                  if (game.lateArrivals) {
                    Object.values(game.lateArrivals).forEach(isLate => {
                      if (isLate) gameLateFees += game.lateFeeAmount ?? 5;
                    });
                  }
                  const total = sharedTotal;

                  const perPlayer = game.playing11?.length > 0 ? sharedTotal / game.playing11.length : 0;

                  return (
                    <div key={game.id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-semibold">{game.name || `${game.homeTeam} vs ${game.awayTeam} `}</div>
                          <div className="text-sm text-gray-500">
                            {formatDate(game.date)} • {game.playing11?.length || 0} players
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-lg">{formatCurrency(total)}</div>
                          <div className="text-sm text-gray-500">
                            ~{formatCurrency(perPlayer)}/player
                          </div>
                        </div>
                      </div>
                      {(gameExpenses.length > 0 || gameLateFees > 0) && (
                        <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                          {gameExpenses.map(expense => (
                            <span key={expense.id} className="text-xs bg-white px-2 py-1 rounded border">
                              {expense.description}: {formatCurrency(expense.amount)}
                            </span>
                          ))}
                          {gameLateFees > 0 && (
                            <span className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded border border-red-100 font-medium">
                              Late Fees: {formatCurrency(gameLateFees)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* All Expenses List */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold mb-4">All Tournament Expenses</h3>
            {tournamentExpenses.length === 0 && stats.totalLateFees === 0 ? (
              <p className="text-gray-500 text-center py-8">No expenses recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Description</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Game</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Added By</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {tournamentExpenses.map(expense => {
                      const game = tournamentGames.find(g => g.id === expense.gameId);
                      return (
                        <tr key={expense.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4">{expense.description}</td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {game ? (game.name || `${game.homeTeam} vs ${game.awayTeam} `) : 'Tournament-wide'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {expense.createdByName || expense.createdBy}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold">
                            {formatCurrency(expense.amount)}
                          </td>
                        </tr>
                      );
                    })}
                    {stats.totalLateFees > 0 && (
                      <>
                        <tr 
                          className="hover:bg-gray-50 cursor-pointer bg-red-50/30"
                          onClick={() => setShowLateFeesDetail(!showLateFeesDetail)}
                        >
                          <td className="py-3 px-4 flex items-center gap-2">
                            {showLateFeesDetail ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            <span className="font-semibold text-red-700">Late Fees</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">Across {stats.lateFeesList.length} late arrivals</td>
                          <td className="py-3 px-4 text-sm text-gray-500">System</td>
                          <td className="py-3 px-4 text-right font-semibold text-red-700">
                            {formatCurrency(stats.totalLateFees)}
                          </td>
                        </tr>
                        {showLateFeesDetail && stats.lateFeesList.map((lf, idx) => {
                          const playerInfo = getPlayerInfo(lf.playerId);
                          return (
                            <tr key={`lf-${idx}`} className="bg-red-50/10 border-l-2 border-red-200">
                              <td className="py-2 px-4 pl-12 text-sm text-gray-600">
                                {playerInfo.name} ({playerInfo.teamName})
                              </td>
                              <td className="py-2 px-4 text-sm text-gray-500">{lf.gameName}</td>
                              <td className="py-2 px-4 text-sm text-gray-500">-</td>
                              <td className="py-2 px-4 text-right text-sm text-gray-600">{formatCurrency(lf.amount)}</td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-100 font-semibold">
                    <tr>
                      <td colSpan="3" className="py-3 px-4">Total</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(stats.totalExpenses)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'players' && (
        <div className="space-y-6">
          {/* Player Summary */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">Player Participation & Finances</h3>
              <span className="text-sm text-gray-500">{stats.totalPlayers} players participated</span>
            </div>

            {playerStats.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users size={64} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Player Data</h3>
                <p>Players will appear here once squads are selected for games.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Player</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Team</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Games Played</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount Owed</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Paid</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Balance</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {playerStats.map(player => {
                       const balance = player.totalOwed - player.paid;
                       const isPaid = balance <= 0.005; // allow penny rounding
                       const isPartial = player.paid > 0 && balance > 0.005;
                       const clubCredit = getClubNetCredit(player.id, player.email);
                       const hasOffsetOpportunity = !isPaid && balance > 0.01 && clubCredit > 0.01;

                       // Tournament-scoped: credit offset waivers applied for THIS tournament only
                       const totalOffsetApplied = walletTransactions
                         .filter(t =>
                           t.isCreditOffset &&
                           t.type === 'waiver' &&
                           t.status === 'approved' &&
                           t.tournamentId === tournament.id &&
                           (t.userId === player.id || (player.email && t.playerEmail?.toLowerCase() === player.email.toLowerCase()))
                         )
                         .reduce((s, t) => s + Math.abs(t.amount || 0), 0);

                       // True only if tournament balance is cleared AND an offset contributed to clearing it
                       const paidViaOffset = isPaid && totalOffsetApplied > 0;

                       return (
                         <React.Fragment key={player.id}>
                            <tr className={`hover:bg-gray-50 ${hasOffsetOpportunity ? 'bg-amber-50/40' : ''} ${player.name === 'Unknown Player' ? 'bg-gray-50/60' : ''}`}>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => togglePlayerCostExpand(player.id)}
                                  className="text-gray-400 hover:text-indigo-600 transition-colors p-1"
                                  title="Toggle details"
                                >
                                  {expandedPlayerCosts[player.id] ? (
                                    <ChevronDown size={16} />
                                  ) : (
                                    <ChevronRight size={16} />
                                  )}
                                </button>
                                {player.name === 'Unknown Player' ? (
                                  <div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-semibold text-gray-400 italic">Unknown Player</span>
                                      {(isAdmin || isTreasurer) && (
                                        <button
                                          onClick={() => openIdentifyModal(player.id)}
                                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full font-semibold hover:bg-indigo-200 transition-colors"
                                          title="Identify this player"
                                        >
                                          <UserCheck size={11} />
                                          Identify
                                        </button>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-400">ID: {player.id.slice(0, 8)}…</div>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="font-semibold">{player.name}</div>
                                    <div className="text-xs text-gray-500">{player.email}</div>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600">{player.teamName}</td>
                            <td className="py-3 px-4 text-center">
                              <span className="font-semibold">{player.gamesPlayed}</span>
                            </td>
                            <td className="py-3 px-4 text-right font-medium">
                              {formatCurrency(player.totalOwed)}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-green-600">
                              {formatCurrency(player.paid)}
                            </td>
                            <td className={`py-3 px-4 text-right font-bold ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(Math.max(0, balance))}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex flex-col items-center gap-1">
                                {isPaid ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-semibold">
                                    <CheckCircle size={12} />
                                    Paid
                                  </span>
                                ) : isPartial ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-semibold">
                                    <AlertCircle size={12} />
                                    Partial
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-semibold">
                                    <XCircle size={12} />
                                    Unpaid
                                  </span>
                                )}

                                {/* Credit offset bracket note — tournament-scoped only */}
                                {paidViaOffset && (
                                  <span className="text-xs text-gray-500 leading-tight text-center">
                                    ({formatCurrency(totalOffsetApplied)} via credit offset)
                                  </span>
                                )}

                                {hasOffsetOpportunity && (isTreasurer || isAdmin) && player.id !== currentUserId && (
                                  <button
                                    onClick={() => openCreditOffsetModal(player)}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500 text-white text-xs rounded-full font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap"
                                    title={`Club owes ${player.name} ${formatCurrency(clubCredit)} — apply as credit toward tournament fees`}
                                  >
                                    ⚡ Apply Credit
                                  </button>
                                )}

                                {/* Mark Tournament Paid — for admin when payment exists but isn't attributed to tournament */}
                                {!isPaid && (isTreasurer || isAdmin) && player.id !== currentUserId && (
                                  <button
                                    onClick={() => openMarkPaidModal(player, balance)}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-xs rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
                                    title="Record payment for this tournament"
                                  >
                                    <CheckCircle size={11} />
                                    Mark Paid
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Collapsible Per-Game Cost Breakdown Row */}
                          {expandedPlayerCosts[player.id] && (
                            <tr className="bg-purple-50/20">
                              <td colSpan={7} className="px-6 py-4">
                                <div className="overflow-x-auto rounded-lg border border-purple-100 bg-white p-4 shadow-sm">
                                  <h4 className="text-xs font-bold text-purple-800 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                    <PieChart size={14} className="text-purple-600" />
                                    Cost Breakdown Per Game
                                  </h4>
                                  {Object.keys(player.breakdown || {}).length === 0 ? (
                                    <div className="text-sm text-gray-500 italic py-2">
                                      No game-by-game breakdown available for this player.
                                    </div>
                                  ) : (
                                    <table className="min-w-full divide-y divide-purple-100 text-xs">
                                      <thead>
                                        <tr className="text-left text-gray-500 font-semibold uppercase">
                                          <th className="py-2 px-3">Game Date</th>
                                          <th className="py-2 px-3">Match</th>
                                          <th className="py-2 px-3 text-right">Game Expenses Share</th>
                                          <th className="py-2 px-3 text-right">Tournament Expenses Share</th>
                                          <th className="py-2 px-3 text-right">Late Fee</th>
                                          <th className="py-2 px-3 text-right font-bold text-gray-700">Total</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100">
                                        {Object.values(player.breakdown)
                                          .sort((a, b) => new Date(a.gameDate) - new Date(b.gameDate))
                                          .map(gb => (
                                            <tr key={gb.gameId} className="hover:bg-purple-50/30 transition-colors">
                                              <td className="py-2 px-3 whitespace-nowrap text-gray-600">
                                                {formatDate(gb.gameDate)}
                                              </td>
                                              <td className="py-2 px-3 font-medium text-gray-800">
                                                {gb.gameName}
                                              </td>
                                              <td className="py-2 px-3 text-right text-gray-600">
                                                {formatCurrency(gb.perGameShare)}
                                              </td>
                                              <td className="py-2 px-3 text-right text-gray-600">
                                                {formatCurrency(gb.tourneyShare)}
                                              </td>
                                              <td className="py-2 px-3 text-right text-gray-600">
                                                {gb.lateFee > 0 ? formatCurrency(gb.lateFee) : '-'}
                                              </td>
                                              <td className="py-2 px-3 text-right font-bold text-gray-900">
                                                {formatCurrency(gb.total)}
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}

                          {hasOffsetOpportunity && (isTreasurer || isAdmin) && player.id !== currentUserId && (
                            <tr className="bg-amber-50">
                              <td colSpan={7} className="px-4 py-2">
                                <div className="flex items-center justify-between gap-4 text-xs text-amber-800">
                                  <span>
                                    💡 Club owes <strong>{player.name}</strong> <strong>{formatCurrency(clubCredit)}</strong> in credits.
                                    This can offset their <strong>{formatCurrency(balance)}</strong> tournament balance
                                    — net result: {formatCurrency(Math.max(0, balance - clubCredit))} remaining due,
                                    club credit reduces to {formatCurrency(Math.max(0, clubCredit - balance))}.
                                  </span>
                                  <button
                                    onClick={() => openCreditOffsetModal(player)}
                                    className="shrink-0 px-3 py-1 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 transition-colors"
                                  >
                                    Apply Credit Offset
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Payment Collection Summary */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="font-semibold text-green-900">Fully Paid</span>
              </div>
              <div className="text-3xl font-bold text-green-700">
                {playerStats.filter(p => (p.totalOwed - p.paid) <= 0).length}
              </div>
              <div className="text-sm text-green-600">players</div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="text-yellow-600" size={20} />
                <span className="font-semibold text-yellow-900">Partial Payment</span>
              </div>
              <div className="text-3xl font-bold text-yellow-700">
                {playerStats.filter(p => p.paid > 0 && (p.totalOwed - p.paid) > 0).length}
              </div>
              <div className="text-sm text-yellow-600">players</div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="text-red-600" size={20} />
                <span className="font-semibold text-red-900">Unpaid</span>
              </div>
              <div className="text-3xl font-bold text-red-700">
                {playerStats.filter(p => p.paid === 0 && p.totalOwed > 0).length}
              </div>
              <div className="text-sm text-red-600">players</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'teams' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-bold mb-4">Participating Teams</h3>
            {teams.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No teams found in club.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {teams.map(team => {
                  const isParticipating = (currentTournament.teams?.includes(team.id)) || (currentTournament.rosters && currentTournament.rosters[team.id]);
                  const roster = currentTournament.rosters?.[team.id] || [];
                  const rosterCount = roster.length;

                  return (
                    <div key={team.id} className={`p-4 rounded-xl border bg-white border-gray-200`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="font-bold text-gray-900">{team.name}</h4>
                          <div className="text-sm text-gray-500">{team.players?.length || 0} total players</div>
                        </div>
                        {isParticipating ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                            Participating
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-full">
                            Not Registered
                          </span>
                        )}
                      </div>

                      {isParticipating && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-2 text-blue-800">
                            <Users size={18} />
                            <span className="font-semibold text-sm">Tournament Roster</span>
                          </div>
                          <span className="font-bold text-xl text-blue-600">{rosterCount}</span>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenRosterModal(team)}
                          className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${isParticipating
                            ? 'bg-white border border-blue-600 text-blue-600 hover:bg-blue-50'
                            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                            }`}
                        >
                          {isParticipating ? 'Manage Roster' : 'Register Team'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'umpiring' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center bg-white rounded-xl shadow-sm p-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Umpiring Assignments</h3>
              <p className="text-sm text-gray-500 mt-1">Games where your club teams are assigned to umpire</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowUmpireAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 font-semibold transition-colors"
              >
                <Plus size={18} />
                Add Assignment
              </button>
              <button
                onClick={() => setShowUmpireImportModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition-colors"
              >
                <Upload size={18} />
                Import Schedule
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {(!currentTournament.umpiringAssignments || currentTournament.umpiringAssignments.length === 0) ? (
              <div className="p-12 text-center text-gray-500">
                <ClipboardList size={48} className="mx-auto mb-3 text-gray-300" />
                <p>No umpiring assignments imported yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Match</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venue</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">Umpiring Team</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-green-700 uppercase tracking-wider">Volunteers</th>
                      <th className="px-6 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentTournament.umpiringAssignments.map((assignment, idx) => (
                      <tr key={assignment.id || idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{assignment.date && formatDate(assignment.date)}</div>
                          <div className="text-sm text-gray-500">{assignment.time}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{assignment.homeTeam} vs {assignment.awayTeam}</div>
                          {assignment.matchType && (
                            <div className="text-xs text-gray-500 italic mt-0.5">{assignment.matchType}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500">{assignment.venue || '-'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {assignment.umpiringTeamName}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {assignment.volunteers && assignment.volunteers.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {assignment.volunteers.map((v, i) => (
                                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium border border-green-200">
                                  {v.userName}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic text-xs">No volunteers yet</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleDeleteUmpiringAssignment(assignment.id, idx)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                            title="Delete Assignment"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <TournamentRosterModal
        isOpen={rosterModalOpen}
        onClose={() => setRosterModalOpen(false)}
        team={selectedTeamForRoster}
        tournament={currentTournament}
        currentRoster={selectedTeamForRoster ? (currentTournament.rosters?.[selectedTeamForRoster.id] || []) : []}
        onSave={handleSaveRoster}
      />

      {/* Credit Offset Confirmation Modal */}
      {showCreditOffsetModal && creditOffsetTarget && (() => {
        const customAmt = parseFloat(creditOffsetAmount) || 0;
        const remainingTournament = Math.max(0, creditOffsetTarget.tournamentBalance - customAmt);
        const remainingCredit = Math.max(0, creditOffsetTarget.clubCredit - customAmt);
        const isAmountValid = customAmt > 0
          && customAmt <= creditOffsetTarget.tournamentBalance + 0.01
          && customAmt <= creditOffsetTarget.clubCredit + 0.01;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-xl">⚡</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Apply Credit Offset</h3>
                  <p className="text-sm text-gray-500">{creditOffsetTarget.player.name}</p>
                </div>
              </div>

              <div className="space-y-3 mb-5">
                {/* Info rows */}
                <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex justify-between text-sm">
                  <span className="text-red-700">Tournament fees owed</span>
                  <span className="font-bold text-red-700">{formatCurrency(creditOffsetTarget.tournamentBalance)}</span>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex justify-between text-sm">
                  <span className="text-blue-700">Club credit available</span>
                  <span className="font-bold text-blue-700">{formatCurrency(creditOffsetTarget.clubCredit)}</span>
                </div>

                {/* Editable offset amount */}
                <div className="border-t pt-3">
                  <label className="block text-xs font-semibold text-amber-800 mb-1">⚡ Offset amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-700 font-semibold">$</span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      max={Math.min(creditOffsetTarget.tournamentBalance, creditOffsetTarget.clubCredit).toFixed(2)}
                      value={creditOffsetAmount}
                      onChange={e => setCreditOffsetAmount(e.target.value)}
                      className="w-full pl-7 pr-4 py-2.5 border border-amber-300 rounded-lg bg-amber-50 text-amber-900 font-bold text-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                      placeholder="0.00"
                    />
                  </div>
                  {!isAmountValid && customAmt > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      Amount must be ≤ {formatCurrency(Math.min(creditOffsetTarget.tournamentBalance, creditOffsetTarget.clubCredit))}
                    </p>
                  )}
                </div>

                {/* Live balance previews */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-gray-50 rounded p-2">
                    <div className="font-medium text-gray-700 mb-0.5">Remaining tournament balance</div>
                    <div className={`font-bold ${remainingTournament <= 0 ? 'text-green-700' : 'text-gray-900'}`}>
                      {formatCurrency(remainingTournament)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="font-medium text-gray-700 mb-0.5">Remaining club credit</div>
                    <div className="font-bold text-gray-900">{formatCurrency(remainingCredit)}</div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Note <span className="font-normal text-gray-400">(optional)</span></label>
                  <textarea
                    rows={2}
                    value={creditOffsetNote}
                    onChange={e => setCreditOffsetNote(e.target.value)}
                    placeholder="e.g. Tournament registration discount agreed on May 21"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                  />
                </div>

                <p className="text-xs text-gray-400 italic">
                  Two transactions will be recorded: a tournament waiver and a reimbursement settlement. Full audit trail preserved.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCreditOffsetModal(false);
                    setCreditOffsetTarget(null);
                    setCreditOffsetAmount('');
                    setCreditOffsetNote('');
                  }}
                  className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={creditOffsetLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreditOffset}
                  disabled={creditOffsetLoading || !isAmountValid}
                  className="flex-1 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors disabled:opacity-40"
                >
                  {creditOffsetLoading ? 'Applying...' : 'Confirm Offset'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Mark Tournament Paid Modal */}
      {showMarkPaidModal && markPaidTarget && (() => {
        const amt = parseFloat(markPaidAmount) || 0;
        const isValid = amt > 0 && amt <= markPaidTarget.balance + 0.01;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Record Tournament Payment</h3>
                  <p className="text-sm text-gray-500">{markPaidTarget.player.name}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Use this when a payment was received but isn't showing in the tournament view
                (e.g. paid via Zelle without tournament attribution).
              </p>

              {/* Amount */}
              <div className="mb-3">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Amount <span className="text-gray-400 font-normal">(outstanding: {formatCurrency(markPaidTarget.balance)})</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">$</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={markPaidTarget.balance.toFixed(2)}
                    value={markPaidAmount}
                    onChange={e => setMarkPaidAmount(e.target.value)}
                    className="w-full pl-7 pr-4 py-2.5 border border-gray-300 rounded-lg text-lg font-bold focus:outline-none focus:ring-2 focus:ring-green-400"
                    autoFocus
                  />
                </div>
                {!isValid && amt > 0 && (
                  <p className="text-xs text-red-600 mt-1">Amount must be ≤ {formatCurrency(markPaidTarget.balance)}</p>
                )}
              </div>

              {/* Note */}
              <div className="mb-5">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Note <span className="font-normal text-gray-400">(optional — e.g. "Paid via Zelle May 26")</span>
                </label>
                <textarea
                  rows={2}
                  value={markPaidNote}
                  onChange={e => setMarkPaidNote(e.target.value)}
                  placeholder="e.g. Paid via Zelle on May 26, 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
                />
              </div>

              <p className="text-xs text-gray-400 italic mb-4">
                This creates a payment record attributed to this tournament. It does not affect the player's global balance — use Payment Management to record the actual payment if not already done.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => { setShowMarkPaidModal(false); setMarkPaidTarget(null); }}
                  className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={markPaidLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkTournamentPaid}
                  disabled={markPaidLoading || !isValid}
                  className="flex-1 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-40"
                >
                  {markPaidLoading ? 'Recording...' : 'Confirm Payment'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Identify Unknown Player Modal */}
      {showIdentifyModal && (() => {
        const filtered = allPlayers
          .filter(p =>
            !identifySearch ||
            p.name?.toLowerCase().includes(identifySearch.toLowerCase()) ||
            p.email?.toLowerCase().includes(identifySearch.toLowerCase())
          )
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <UserCheck size={20} className="text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Identify Player</h3>
                  <p className="text-xs text-gray-400">Ghost ID: {identifyGhostId?.slice(0, 12)}…</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-3">
                Select the club member who should be assigned the dues for this unknown player.
                Their ID will be updated across all tournament games.
              </p>

              <input
                type="text"
                placeholder="Search by name or email…"
                value={identifySearch}
                onChange={e => setIdentifySearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                autoFocus
              />

              <div className="max-h-64 overflow-y-auto divide-y border border-gray-200 rounded-lg">
                {filtered.length === 0 ? (
                  <p className="p-4 text-sm text-gray-400 text-center">No players found</p>
                ) : filtered.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleIdentifyPlayer(p)}
                    disabled={identifyLoading}
                    className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors flex items-center justify-between group disabled:opacity-50"
                  >
                    <div>
                      <div className="font-semibold text-gray-900 group-hover:text-indigo-700">{p.name}</div>
                      <div className="text-xs text-gray-500">{p.email}</div>
                    </div>
                    <UserCheck size={16} className="text-gray-300 group-hover:text-indigo-500" />
                  </button>
                ))}
              </div>

              <button
                onClick={() => { setShowIdentifyModal(false); setIdentifyGhostId(null); }}
                className="mt-4 w-full py-2.5 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                disabled={identifyLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        );
      })()}

      {/* Import Games Modal */}
      {onImportGames && (
        <ImportScheduleModal
          isOpen={showImportGamesModal}
          onClose={() => setShowImportGamesModal(false)}
          teams={teams}
          tournaments={[tournament]}
          onImportGames={onImportGames}
          defaultTournamentId={tournament.id}
        />
      )}

      <ImportUmpireScheduleModal
        isOpen={showUmpireImportModal}
        onClose={() => setShowUmpireImportModal(false)}
        teams={teams}
        tournamentId={tournament.id}
        onImportAssignments={handleImportUmpiringAssignments}
      />

      <AddUmpireAssignmentModal
        isOpen={showUmpireAddModal}
        onClose={() => setShowUmpireAddModal(false)}
        teams={teams}
        onAddAssignment={handleAddUmpiringAssignment}
      />
    </div>
  );
};

export default TournamentDetailView;